/**
 * Repetição espaçada.
 *
 * Paridade com a rota `/api/responder` do Flask em produção, com duas correcções
 * documentadas nas migrações 14–15:
 *
 *  - Grade 2 (Difícil): crescimento mínimo de 1 dia (como o Anki), porque
 *    `int(atual * 1.2)` estagnava entre 1 e 4 dias.
 *  - Grades 2–4: `proxima_revisao` ancora na meia-noite BR do dia alvo, não
 *    em `now + N * 86400000`.
 *
 * `Math.trunc` reproduz o `int()` do Python (trunca em direcção a zero).
 */

import {
  brasilDayString,
  inicioDoDiaBrasilEmUtc,
  somarDiasBrasil,
} from './dates.js';

/** 1 = Errei, 2 = Difícil, 3 = Bom, 4 = Fácil. */
export type ReviewGrade = 1 | 2 | 3 | 4;

export const AGAIN_DELAY_MINUTES = 10;

/** Tecto de intervalo — evita estourar `Date` após muitos “Fácil” seguidos. */
export const INTERVALO_MAXIMO_DIAS = 36500;

export interface ReviewState {
  /** Intervalo actual em dias. */
  intervalo: number;
  proximaRevisao: Date | null;
}

export interface SrsOutcome {
  intervalo: number;
  proximaRevisao: Date;
  /** Guardado em `logs_reviews.acerto`. */
  acerto: boolean;
}

export function isReviewGrade(value: unknown): value is ReviewGrade {
  return value === 1 || value === 2 || value === 3 || value === 4;
}

function limitarIntervalo(dias: number): number {
  return Math.min(INTERVALO_MAXIMO_DIAS, dias);
}

/** Meia-noite BR do dia `hojeBR + intervalo` dias. */
function proximaRevisaoEmDias(intervalo: number, now: Date): Date {
  const diaAlvo = somarDiasBrasil(brasilDayString(now), intervalo);
  return inicioDoDiaBrasilEmUtc(diaAlvo);
}

export function scheduleReview(
  current: Pick<ReviewState, 'intervalo'> | null,
  grade: ReviewGrade,
  now: Date = new Date(),
): SrsOutcome {
  const atual = current?.intervalo ?? 0;

  if (grade === 1) {
    return {
      intervalo: 0,
      proximaRevisao: new Date(now.getTime() + AGAIN_DELAY_MINUTES * 60_000),
      acerto: false,
    };
  }

  let intervalo: number;
  if (grade === 2) {
    intervalo = limitarIntervalo(
      Math.max(1, Math.max(atual + 1, Math.trunc(atual * 1.2))),
    );
  } else if (grade === 3) {
    intervalo = limitarIntervalo(
      atual > 0 ? Math.max(1, Math.trunc(atual * 2.5)) : 1,
    );
  } else {
    intervalo = limitarIntervalo(Math.max(4, Math.trunc(atual * 3.5)));
  }

  return {
    intervalo,
    proximaRevisao: proximaRevisaoEmDias(intervalo, now),
    acerto: true,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Streak
// ─────────────────────────────────────────────────────────────────────────────

export interface StreakState {
  streak: number;
  /** Dia civil brasileiro em `YYYY-MM-DD`. */
  ultimaRevisao: string | null;
}

/**
 * Mesma regra do Flask: estudar no dia seguinte incrementa; qualquer buraco
 * reinicia em 1; estudar duas vezes no mesmo dia não muda nada.
 */
export function advanceStreak(
  state: StreakState | null,
  hoje: string,
): StreakState {
  if (!state) {
    return { streak: 1, ultimaRevisao: hoje };
  }
  if (state.ultimaRevisao === hoje) {
    return state;
  }
  const ontem = previousDay(hoje);
  return {
    streak: state.ultimaRevisao === ontem ? state.streak + 1 : 1,
    ultimaRevisao: hoje,
  };
}

/** Dia anterior a uma data `YYYY-MM-DD`, sem depender de fuso. */
export function previousDay(day: string): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() - 1);
  return dt.toISOString().slice(0, 10);
}

/** Nota do simulado, de 0 a 10. */
export function simuladoScore(acertos: number, total: number): number {
  if (total <= 0) return 0;
  return (acertos / total) * 10;
}
